import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  Calendar,
  Target,
  Zap,
  CheckCircle2,
  BarChart3,
  ArrowLeft,
  Check,
  Brain,
  TrendingUp,
  Flame,
  Star
} from 'lucide-react';
import api from '../lib/axios';
import { toast } from 'sonner';

// Using centralized api

function IkonsWeek() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('plan');
  const [loading, setLoading] = useState(false);

  // Get current week's Monday
  const getWeekStart = () => {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(now.setDate(diff)).toISOString().split('T')[0];
  };

  // Weekly Plan State (IK-06)
  const [weeklyPlan, setWeeklyPlan] = useState({
    week_start: getWeekStart(),
    match_learning: '',
    weekly_focus: '',
    how_to_work: '',
    daily_microhabit: '',
    week_check_completed: false,
    week_check_reflection: ''
  });

  // Daily Standard State (IK-07)
  const [dailyStandard, setDailyStandard] = useState({
    week_start: getWeekStart(),
    days: [
      { day: 'lunes', trained_professionally: false, competed: false },
      { day: 'martes', trained_professionally: false, competed: false },
      { day: 'miércoles', trained_professionally: false, competed: false },
      { day: 'jueves', trained_professionally: false, competed: false },
      { day: 'viernes', trained_professionally: false, competed: false },
      { day: 'sábado', trained_professionally: false, competed: false },
      { day: 'domingo', trained_professionally: false, competed: false }
    ],
    weekly_reflection: ''
  });

  // Player Score State (IK-08)
  const [playerScore, setPlayerScore] = useState({
    week_start: getWeekStart(),
    competitive_level: 3,
    mentality: 3,
    decision_making: 3,
    consistency: 3,
    evolution: 3,
    improvement_needed: '',
    next_week_focus: ''
  });

  const totalScore = playerScore.competitive_level + playerScore.mentality + 
    playerScore.decision_making + playerScore.consistency + playerScore.evolution;

  const getScoreStatus = () => {
    if (totalScore >= 20) return { color: 'green', label: 'Nivel Alto', bg: 'bg-green-100 border-green-300' };
    if (totalScore >= 15) return { color: 'yellow', label: 'En Desarrollo', bg: 'bg-yellow-100 border-yellow-300' };
    return { color: 'red', label: 'Alerta', bg: 'bg-red-100 border-red-300' };
  };

  const getStandardProgress = () => {
    const totalChecks = dailyStandard.days.reduce((acc, day) => {
      return acc + (day.trained_professionally ? 1 : 0) + (day.competed ? 1 : 0);
    }, 0);
    return (totalChecks / 14) * 100;
  };

  const handleSubmitPlan = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/ikons/weekly-plans', weeklyPlan);
      toast.success('Plan semanal guardado');
      setActiveTab('standard');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitStandard = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/ikons/daily-standards', dailyStandard);
      toast.success('Estándar diario guardado');
      setActiveTab('score');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitScore = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/ikons/player-scores', playerScore);
      toast.success('Player Score guardado');
      navigate('/ikons');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const updateDay = (index, field, value) => {
    const newDays = [...dailyStandard.days];
    newDays[index] = { ...newDays[index], [field]: value };
    setDailyStandard({ ...dailyStandard, days: newDays });
  };

  const status = getScoreStatus();

  return (
    <Layout>
      <div className="max-w-4xl mx-auto" data-testid="ikons-week">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/ikons')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-xl">
              <Calendar className="h-8 w-8 text-green-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-[#4F4F4F]">Plan Semanal</h1>
              <p className="text-[#808080]">Tu semana de desarrollo profesional</p>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="plan" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Plan
            </TabsTrigger>
            <TabsTrigger value="standard" className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Estándar
            </TabsTrigger>
            <TabsTrigger value="score" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Score
            </TabsTrigger>
          </TabsList>

          {/* IK-06: Plan Semanal Ikons */}
          <TabsContent value="plan">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-green-600" />
                  IK-06: Plan Semanal Ikons
                </CardTitle>
                <CardDescription>
                  Define tu foco de desarrollo para la semana
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmitPlan} className="space-y-6">
                  <div>
                    <Label>Semana del</Label>
                    <Input 
                      type="date" 
                      value={weeklyPlan.week_start}
                      onChange={(e) => setWeeklyPlan({...weeklyPlan, week_start: e.target.value})}
                      className="mt-1 w-48"
                    />
                  </div>

                  <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
                    <Label className="flex items-center gap-2 text-blue-700 mb-2">
                      <Brain className="h-5 w-5" />
                      Aprendizaje del Partido
                    </Label>
                    <Textarea 
                      value={weeklyPlan.match_learning}
                      onChange={(e) => setWeeklyPlan({...weeklyPlan, match_learning: e.target.value})}
                      placeholder="¿Qué aprendí del último partido? (Lo que me llevo)"
                      required
                      rows={3}
                    />
                  </div>

                  <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                    <Label className="flex items-center gap-2 text-amber-700 mb-2">
                      <Target className="h-5 w-5" />
                      Foco Semanal (UNO)
                    </Label>
                    <Input 
                      value={weeklyPlan.weekly_focus}
                      onChange={(e) => setWeeklyPlan({...weeklyPlan, weekly_focus: e.target.value})}
                      placeholder="Ej: 'Ser más agresivo sin balón' o 'Mejorar mi pase largo'"
                      required
                    />
                    <p className="text-xs text-amber-600 mt-1">
                      Un solo foco. No intentes mejorar todo a la vez.
                    </p>
                  </div>

                  <div className="p-4 rounded-xl bg-green-50 border border-green-200">
                    <Label className="flex items-center gap-2 text-green-700 mb-2">
                      <Zap className="h-5 w-5" />
                      ¿Cómo lo voy a trabajar?
                    </Label>
                    <Textarea 
                      value={weeklyPlan.how_to_work}
                      onChange={(e) => setWeeklyPlan({...weeklyPlan, how_to_work: e.target.value})}
                      placeholder="Acciones concretas: qué, cuándo, cuántas veces"
                      required
                      rows={3}
                    />
                  </div>

                  <div className="p-4 rounded-xl bg-purple-50 border border-purple-200">
                    <Label className="flex items-center gap-2 text-purple-700 mb-2">
                      <Flame className="h-5 w-5" />
                      Micro-Hábito Diario
                    </Label>
                    <Input 
                      value={weeklyPlan.daily_microhabit}
                      onChange={(e) => setWeeklyPlan({...weeklyPlan, daily_microhabit: e.target.value})}
                      placeholder="Ej: '5 min de visualización antes de dormir'"
                      required
                    />
                    <p className="text-xs text-purple-600 mt-1">
                      Algo pequeño que puedas hacer TODOS los días
                    </p>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-green-600 hover:bg-green-700"
                    disabled={loading}
                  >
                    {loading ? 'Guardando...' : 'Guardar Plan y Continuar →'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* IK-07: Check de Estándar Diario */}
          <TabsContent value="standard">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  IK-07: Check de Estándar Diario
                </CardTitle>
                <CardDescription>
                  ¿Estás manteniendo tu estándar profesional?
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmitStandard} className="space-y-6">
                  {/* Progress Bar */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Progreso semanal</span>
                      <span className="font-medium">{Math.round(getStandardProgress())}%</span>
                    </div>
                    <Progress value={getStandardProgress()} className="h-3" />
                  </div>

                  {/* Days Grid */}
                  <div className="space-y-3">
                    {dailyStandard.days.map((day, index) => (
                      <div 
                        key={day.day} 
                        className={`p-4 rounded-xl border ${
                          day.trained_professionally && day.competed 
                            ? 'bg-green-50 border-green-200' 
                            : day.trained_professionally || day.competed
                            ? 'bg-yellow-50 border-yellow-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium capitalize w-24">{day.day}</span>
                          
                          <div className="flex items-center gap-6">
                            <div className="flex items-center gap-2">
                              <Label className="text-sm">Entrené como profesional</Label>
                              <Switch 
                                checked={day.trained_professionally}
                                onCheckedChange={(checked) => updateDay(index, 'trained_professionally', checked)}
                              />
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <Label className="text-sm">Competí</Label>
                              <Switch 
                                checked={day.competed}
                                onCheckedChange={(checked) => updateDay(index, 'competed', checked)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="p-4 rounded-xl bg-gray-50 border">
                    <Label className="mb-2 block">Reflexión de la semana (opcional)</Label>
                    <Textarea 
                      value={dailyStandard.weekly_reflection}
                      onChange={(e) => setDailyStandard({...dailyStandard, weekly_reflection: e.target.value})}
                      placeholder="¿Qué aprendiste de tu consistencia esta semana?"
                      rows={2}
                    />
                  </div>

                  <div className="bg-gray-100 p-4 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground italic">
                      "El talento te abre puertas. El estándar te mantiene dentro."
                    </p>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-green-600 hover:bg-green-700"
                    disabled={loading}
                  >
                    {loading ? 'Guardando...' : 'Guardar Estándar y Continuar →'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* IK-08: Ikons Player Score */}
          <TabsContent value="score">
            <Card className={`border-2 ${status.bg}`}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-green-600" />
                  IK-08: Ikons Player Score
                </CardTitle>
                <CardDescription>
                  Tu evaluación semanal de desarrollo
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmitScore} className="space-y-8">
                  {/* Score Display */}
                  <div className="text-center p-6 rounded-xl bg-white border-2">
                    <div className="text-6xl font-bold text-[#4F4F4F] mb-2">{totalScore}</div>
                    <div className="text-2xl text-muted-foreground mb-4">/25</div>
                    <Badge className={`text-lg px-4 py-1 ${
                      status.color === 'green' ? 'bg-green-500' : 
                      status.color === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'
                    } text-white`}>
                      {status.label}
                    </Badge>
                  </div>

                  {/* Score Sliders */}
                  <div className="space-y-6">
                    {/* Competitive Level */}
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <Label className="flex items-center gap-2">
                          <Flame className="h-4 w-4 text-orange-500" />
                          Nivel Competitivo
                        </Label>
                        <Badge variant="outline">{playerScore.competitive_level}/5</Badge>
                      </div>
                      <Slider
                        value={[playerScore.competitive_level]}
                        onValueChange={(v) => setPlayerScore({...playerScore, competitive_level: v[0]})}
                        max={5}
                        min={1}
                        step={1}
                      />
                      <p className="text-xs text-muted-foreground">¿Cuánto me exigí esta semana?</p>
                    </div>

                    {/* Mentality */}
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <Label className="flex items-center gap-2">
                          <Brain className="h-4 w-4 text-purple-500" />
                          Mentalidad
                        </Label>
                        <Badge variant="outline">{playerScore.mentality}/5</Badge>
                      </div>
                      <Slider
                        value={[playerScore.mentality]}
                        onValueChange={(v) => setPlayerScore({...playerScore, mentality: v[0]})}
                        max={5}
                        min={1}
                        step={1}
                      />
                      <p className="text-xs text-muted-foreground">¿Cómo gestioné los momentos difíciles?</p>
                    </div>

                    {/* Decision Making */}
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <Label className="flex items-center gap-2">
                          <Target className="h-4 w-4 text-blue-500" />
                          Toma de Decisiones
                        </Label>
                        <Badge variant="outline">{playerScore.decision_making}/5</Badge>
                      </div>
                      <Slider
                        value={[playerScore.decision_making]}
                        onValueChange={(v) => setPlayerScore({...playerScore, decision_making: v[0]})}
                        max={5}
                        min={1}
                        step={1}
                      />
                      <p className="text-xs text-muted-foreground">¿Tomé buenas decisiones en partido y entrenamientos?</p>
                    </div>

                    {/* Consistency */}
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <Label className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          Consistencia
                        </Label>
                        <Badge variant="outline">{playerScore.consistency}/5</Badge>
                      </div>
                      <Slider
                        value={[playerScore.consistency]}
                        onValueChange={(v) => setPlayerScore({...playerScore, consistency: v[0]})}
                        max={5}
                        min={1}
                        step={1}
                      />
                      <p className="text-xs text-muted-foreground">¿Mantuve mi nivel durante toda la semana?</p>
                    </div>

                    {/* Evolution */}
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <Label className="flex items-center gap-2">
                          <TrendingUp className="h-4 w-4 text-amber-500" />
                          Evolución
                        </Label>
                        <Badge variant="outline">{playerScore.evolution}/5</Badge>
                      </div>
                      <Slider
                        value={[playerScore.evolution]}
                        onValueChange={(v) => setPlayerScore({...playerScore, evolution: v[0]})}
                        max={5}
                        min={1}
                        step={1}
                      />
                      <p className="text-xs text-muted-foreground">¿Mejoré respecto a la semana pasada?</p>
                    </div>
                  </div>

                  {/* Improvement Section */}
                  <div className="space-y-4">
                    <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                      <Label className="text-amber-700 mb-2 block">¿Qué necesito mejorar?</Label>
                      <Textarea 
                        value={playerScore.improvement_needed}
                        onChange={(e) => setPlayerScore({...playerScore, improvement_needed: e.target.value})}
                        placeholder="Sé específico: qué área y por qué"
                        required
                        rows={2}
                      />
                    </div>

                    <div className="p-4 rounded-xl bg-green-50 border border-green-200">
                      <Label className="text-green-700 mb-2 block">Foco para la próxima semana</Label>
                      <Input 
                        value={playerScore.next_week_focus}
                        onChange={(e) => setPlayerScore({...playerScore, next_week_focus: e.target.value})}
                        placeholder="Un foco claro y medible"
                        required
                      />
                    </div>
                  </div>

                  <div className="bg-gray-100 p-4 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground italic">
                      "El progreso no es lineal. Lo importante es la dirección."
                    </p>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-green-600 hover:bg-green-700"
                    disabled={loading}
                  >
                    <Check className="h-4 w-4 mr-2" />
                    {loading ? 'Guardando...' : 'Guardar Player Score'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

export default IkonsWeek;
